import React, { useEffect, useRef, useReducer, useDebugValue, useMemo, useCallback } from 'react';
let PropTypes;

try {
  PropTypes = require("prop-types");
} catch (e) {}

const childrenFn = PropTypes && PropTypes.oneOfType([PropTypes.node, PropTypes.func]);
const stateObject = PropTypes && PropTypes.shape({
  initialValue: PropTypes.any,
  data: PropTypes.any,
  error: PropTypes.instanceOf(Error),
  value: PropTypes.any,
  startedAt: PropTypes.instanceOf(Date),
  finishedAt: PropTypes.instanceOf(Date),
  status: PropTypes.oneOf(["initial", "pending", "fulfilled", "rejected"]),
  isInitial: PropTypes.bool,
  isPending: PropTypes.bool,
  isLoading: PropTypes.bool,
  isFulfilled: PropTypes.bool,
  isResolved: PropTypes.bool,
  isRejected: PropTypes.bool,
  isSettled: PropTypes.bool,
  counter: PropTypes.number,
  cancel: PropTypes.func,
  run: PropTypes.func,
  reload: PropTypes.func,
  setData: PropTypes.func,
  setError: PropTypes.func
});
var propTypes = PropTypes && {
  Async: {
    children: childrenFn,
    promise: PropTypes.instanceOf(Promise),
    promiseFn: PropTypes.func,
    deferFn: PropTypes.func,
    watch: PropTypes.any,
    watchFn: PropTypes.func,
    initialValue: PropTypes.any,
    onResolve: PropTypes.func,
    onReject: PropTypes.func,
    reducer: PropTypes.func,
    dispatcher: PropTypes.func
  },
  Initial: {
    children: childrenFn.isRequired,
    state: stateObject.isRequired,
    persist: PropTypes.bool
  },
  Pending: {
    children: childrenFn.isRequired,
    state: stateObject.isRequired,
    initial: PropTypes.bool
  },
  Fulfilled: {
    children: childrenFn.isRequired,
    state: stateObject.isRequired,
    persist: PropTypes.bool
  },
  Rejected: {
    children: childrenFn.isRequired,
    state: stateObject.isRequired,
    persist: PropTypes.bool
  },
  Settled: {
    children: childrenFn.isRequired,
    state: stateObject.isRequired,
    persist: PropTypes.bool
  }
};

const nullify = children => children === undefined ? null : children;

const renderFn = function renderFn(children) {
  for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    args[_key - 1] = arguments[_key];
  }

  return nullify(typeof children === "function" ? children(...args) : children);
};
/**
 * Renders only when no promise has started or completed yet.
 *
 * @prop {Function|Node} children Function (passing state) or React node
 * @prop {Object} state React Async state object
 * @prop {boolean} persist Show until we have data, even while pending (loading) or when an error occurred
 */


const Initial = _ref => {
  let children = _ref.children,
      persist = _ref.persist,
      state = _ref.state;
  return state.isInitial || persist && !state.data ? renderFn(children, state) : null;
};
/**
 * Renders only while pending (promise is loading).
 *
 * @prop {Function|Node} children Function (passing state) or React node
 * @prop {Object} state React Async state object
 * @prop {boolean} initial Show only on initial load (data is undefined)
 */


const Pending = _ref2 => {
  let children = _ref2.children,
      initial = _ref2.initial,
      state = _ref2.state;
  return state.isPending && (!initial || !state.value) ? renderFn(children, state) : null;
};
/**
 * Renders only when promise is resolved.
 *
 * @prop {Function|Node} children Function (passing data and state) or React node
 * @prop {Object} state React Async state object
 * @prop {boolean} persist Show old data while pending (promise is loading)
 */


const Fulfilled = _ref3 => {
  let children = _ref3.children,
      persist = _ref3.persist,
      state = _ref3.state;
  return state.isFulfilled || persist && state.data ? renderFn(children, state.data, state) : null;
};
/**
 * Renders only when promise is rejected.
 *
 * @prop {Function|Node} children Function (passing error and state) or React node
 * @prop {Object} state React Async state object
 * @prop {boolean} persist Show old error while pending (promise is loading)
 */


const Rejected = _ref4 => {
  let children = _ref4.children,
      persist = _ref4.persist,
      state = _ref4.state;
  return state.isRejected || persist && state.error ? renderFn(children, state.error, state) : null;
};
/**
 * Renders only when promise is fulfilled or rejected.
 *
 * @prop {Function|Node} children Function (passing state) or React node
 * @prop {Object} state React Async state object
 * @prop {boolean} persist Show old data or error while pending (promise is loading)
 */


const Settled = _ref5 => {
  let children = _ref5.children,
      persist = _ref5.persist,
      state = _ref5.state;
  return state.isSettled || persist && state.value ? renderFn(children, state) : null;
};

if (propTypes) {
  Initial.propTypes = propTypes.Initial;
  Pending.propTypes = propTypes.Pending;
  Fulfilled.propTypes = propTypes.Fulfilled;
  Rejected.propTypes = propTypes.Rejected;
  Settled.propTypes = propTypes.Settled;
}

const statusTypes = {
  initial: "initial",
  pending: "pending",
  fulfilled: "fulfilled",
  rejected: "rejected"
};

const getInitialStatus = (value, promise) => {
  if (value instanceof Error) return statusTypes.rejected;
  if (value !== undefined) return statusTypes.fulfilled;
  if (promise) return statusTypes.pending;
  return statusTypes.initial;
};

const getIdleStatus = value => {
  if (value instanceof Error) return statusTypes.rejected;
  if (value !== undefined) return statusTypes.fulfilled;
  return statusTypes.initial;
};

const getStatusProps = status => ({
  status,
  isInitial: status === statusTypes.initial,
  isPending: status === statusTypes.pending,
  isLoading: status === statusTypes.pending,
  // alias
  isFulfilled: status === statusTypes.fulfilled,
  isResolved: status === statusTypes.fulfilled,
  // alias
  isRejected: status === statusTypes.rejected,
  isSettled: status === statusTypes.fulfilled || status === statusTypes.rejected
});

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};
    var ownKeys = Object.keys(source);

    if (typeof Object.getOwnPropertySymbols === 'function') {
      ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function (sym) {
        return Object.getOwnPropertyDescriptor(source, sym).enumerable;
      }));
    }

    ownKeys.forEach(function (key) {
      _defineProperty(target, key, source[key]);
    });
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

const actionTypes = {
  start: "start",
  cancel: "cancel",
  fulfill: "fulfill",
  reject: "reject"
};

const init = _ref => {
  let initialValue = _ref.initialValue,
      promise = _ref.promise,
      promiseFn = _ref.promiseFn;
  return _objectSpread({
    initialValue,
    data: initialValue instanceof Error ? undefined : initialValue,
    error: initialValue instanceof Error ? initialValue : undefined,
    value: initialValue,
    startedAt: promise || promiseFn ? new Date() : undefined,
    finishedAt: initialValue ? new Date() : undefined
  }, getStatusProps(getInitialStatus(initialValue, promise || promiseFn)), {
    counter: 0
  });
};

const reducer = (state, _ref2) => {
  let type = _ref2.type,
      payload = _ref2.payload,
      meta = _ref2.meta;

  switch (type) {
    case actionTypes.start:
      return _objectSpread({}, state, {
        startedAt: new Date(),
        finishedAt: undefined
      }, getStatusProps(statusTypes.pending), {
        counter: meta.counter
      });

    case actionTypes.cancel:
      return _objectSpread({}, state, {
        startedAt: undefined,
        finishedAt: undefined
      }, getStatusProps(getIdleStatus(state.error || state.data)), {
        counter: meta.counter
      });

    case actionTypes.fulfill:
      return _objectSpread({}, state, {
        data: payload,
        value: payload,
        error: undefined,
        finishedAt: new Date()
      }, getStatusProps(statusTypes.fulfilled));

    case actionTypes.reject:
      return _objectSpread({}, state, {
        error: payload,
        value: payload,
        finishedAt: new Date()
      }, getStatusProps(statusTypes.rejected));

    default:
      return state;
  }
};

const dispatchMiddleware = dispatch => function (action) {
  for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    args[_key - 1] = arguments[_key];
  }

  dispatch(action, ...args);

  if (action.type === actionTypes.start && typeof action.payload === "function") {
    action.payload();
  }
};

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectSpread$1(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};
    var ownKeys = Object.keys(source);

    if (typeof Object.getOwnPropertySymbols === 'function') {
      ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function (sym) {
        return Object.getOwnPropertyDescriptor(source, sym).enumerable;
      }));
    }

    ownKeys.forEach(function (key) {
      _defineProperty$1(target, key, source[key]);
    });
  }

  return target;
}

function _defineProperty$1(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}
/**
 * createInstance allows you to create instances of Async that are bound to a specific promise.
 * A unique instance also uses its own React context for better nesting capability.
 */


const createInstance = function createInstance() {
  let defaultProps = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  let displayName = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : "Async";

  const _React$createContext = React.createContext(),
        Consumer = _React$createContext.Consumer,
        Provider = _React$createContext.Provider;

  class Async extends React.Component {
    constructor(props) {
      super(props);
      this.start = this.start.bind(this);
      this.load = this.load.bind(this);
      this.run = this.run.bind(this);
      this.cancel = this.cancel.bind(this);
      this.onResolve = this.onResolve.bind(this);
      this.onReject = this.onReject.bind(this);
      this.setData = this.setData.bind(this);
      this.setError = this.setError.bind(this);
      const promise = props.promise;
      const promiseFn = props.promiseFn || defaultProps.promiseFn;
      const initialValue = props.initialValue || defaultProps.initialValue;
      this.mounted = false;
      this.counter = 0;
      this.args = [];
      this.abortController = {
        abort: () => {}
      };
      this.state = _objectSpread$1({}, init({
        initialValue,
        promise,
        promiseFn
      }), {
        cancel: this.cancel,
        run: this.run,
        reload: () => {
          this.load();
          this.run(...this.args);
        },
        setData: this.setData,
        setError: this.setError
      });

      const _reducer = props.reducer || defaultProps.reducer;

      const _dispatcher = props.dispatcher || defaultProps.dispatcher;

      const reducer$1 = _reducer ? (state, action) => _reducer(state, action, reducer) : reducer;
      const dispatch = dispatchMiddleware((action, callback) => {
        this.setState(state => reducer$1(state, action), callback);
      });
      this.dispatch = _dispatcher ? action => _dispatcher(action, dispatch, props) : dispatch;
    }

    componentDidMount() {
      this.mounted = true;

      if (this.props.promise || !this.state.initialValue) {
        this.load();
      }
    }

    componentDidUpdate(prevProps) {
      const _this$props = this.props,
            watch = _this$props.watch,
            _this$props$watchFn = _this$props.watchFn,
            watchFn = _this$props$watchFn === void 0 ? defaultProps.watchFn : _this$props$watchFn,
            promise = _this$props.promise,
            promiseFn = _this$props.promiseFn;
      if (watch !== prevProps.watch) this.load();
      if (watchFn && watchFn(_objectSpread$1({}, defaultProps, this.props), _objectSpread$1({}, defaultProps, prevProps))) this.load();

      if (promise !== prevProps.promise) {
        if (promise) this.load();else this.cancel();
      }

      if (promiseFn !== prevProps.promiseFn) {
        if (promiseFn) this.load();else this.cancel();
      }
    }

    componentWillUnmount() {
      this.cancel();
      this.mounted = false;
    }

    getMeta(meta) {
      return _objectSpread$1({
        counter: this.counter
      }, meta);
    }

    start(promiseFn) {
      if ("AbortController" in window) {
        this.abortController.abort();
        this.abortController = new window.AbortController();
      }

      this.counter++;
      return new Promise((resolve, reject) => {
        if (!this.mounted) return;

        const executor = () => promiseFn().then(resolve, reject);

        this.dispatch({
          type: actionTypes.start,
          payload: executor,
          meta: this.getMeta()
        });
      });
    }

    load() {
      const promise = this.props.promise;

      if (promise) {
        return this.start(() => promise).then(this.onResolve(this.counter), this.onReject(this.counter));
      }

      const promiseFn = this.props.promiseFn || defaultProps.promiseFn;

      if (promiseFn) {
        const props = _objectSpread$1({}, defaultProps, this.props);

        return this.start(() => promiseFn(props, this.abortController)).then(this.onResolve(this.counter), this.onReject(this.counter));
      }
    }

    run() {
      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }

      const deferFn = this.props.deferFn || defaultProps.deferFn;

      if (deferFn) {
        this.args = args;

        const props = _objectSpread$1({}, defaultProps, this.props);

        return this.start(() => deferFn(args, props, this.abortController)).then(this.onResolve(this.counter), this.onReject(this.counter));
      }
    }

    cancel() {
      this.counter++;
      this.abortController.abort();
      this.mounted && this.dispatch({
        type: actionTypes.cancel,
        meta: this.getMeta()
      });
    }

    onResolve(counter) {
      return data => {
        if (this.counter === counter) {
          const onResolve = this.props.onResolve || defaultProps.onResolve;
          this.setData(data, () => onResolve && onResolve(data));
        }

        return data;
      };
    }

    onReject(counter) {
      return error => {
        if (this.counter === counter) {
          const onReject = this.props.onReject || defaultProps.onReject;
          this.setError(error, () => onReject && onReject(error));
        }

        return error;
      };
    }

    setData(data, callback) {
      this.mounted && this.dispatch({
        type: actionTypes.fulfill,
        payload: data,
        meta: this.getMeta()
      }, callback);
      return data;
    }

    setError(error, callback) {
      this.mounted && this.dispatch({
        type: actionTypes.reject,
        payload: error,
        error: true,
        meta: this.getMeta()
      }, callback);
      return error;
    }

    render() {
      const children = this.props.children;

      if (typeof children === "function") {
        return React.createElement(Provider, {
          value: this.state
        }, children(this.state));
      }

      if (children !== undefined && children !== null) {
        return React.createElement(Provider, {
          value: this.state
        }, children);
      }

      return null;
    }

  }

  if (propTypes) Async.propTypes = propTypes.Async;

  const AsyncInitial = props => React.createElement(Consumer, null, st => React.createElement(Initial, _extends({}, props, {
    state: st
  })));

  const AsyncPending = props => React.createElement(Consumer, null, st => React.createElement(Pending, _extends({}, props, {
    state: st
  })));

  const AsyncFulfilled = props => React.createElement(Consumer, null, st => React.createElement(Fulfilled, _extends({}, props, {
    state: st
  })));

  const AsyncRejected = props => React.createElement(Consumer, null, st => React.createElement(Rejected, _extends({}, props, {
    state: st
  })));

  const AsyncSettled = props => React.createElement(Consumer, null, st => React.createElement(Settled, _extends({}, props, {
    state: st
  })));

  AsyncInitial.displayName = "".concat(displayName, ".Initial");
  AsyncPending.displayName = "".concat(displayName, ".Pending");
  AsyncFulfilled.displayName = "".concat(displayName, ".Fulfilled");
  AsyncRejected.displayName = "".concat(displayName, ".Rejected");
  AsyncSettled.displayName = "".concat(displayName, ".Settled");
  Async.displayName = displayName;
  Async.Initial = AsyncInitial;
  Async.Pending = AsyncPending;
  Async.Loading = AsyncPending; // alias

  Async.Fulfilled = AsyncFulfilled;
  Async.Resolved = AsyncFulfilled; // alias

  Async.Rejected = AsyncRejected;
  Async.Settled = AsyncSettled;
  return Async;
};

var Async = createInstance();

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance");
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectSpread$2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};
    var ownKeys = Object.keys(source);

    if (typeof Object.getOwnPropertySymbols === 'function') {
      ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function (sym) {
        return Object.getOwnPropertyDescriptor(source, sym).enumerable;
      }));
    }

    ownKeys.forEach(function (key) {
      _defineProperty$2(target, key, source[key]);
    });
  }

  return target;
}

function _defineProperty$2(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

const noop = () => {};

const useAsync = (arg1, arg2) => {
  const options = typeof arg1 === "function" ? _objectSpread$2({}, arg2, {
    promiseFn: arg1
  }) : arg1;
  const counter = useRef(0);
  const isMounted = useRef(true);
  const lastArgs = useRef(undefined);
  const prevOptions = useRef(undefined);
  const abortController = useRef({
    abort: noop
  });
  const reducer$1 = options.reducer,
        dispatcher = options.dispatcher;

  const _useReducer = useReducer(reducer$1 ? (state, action) => reducer$1(state, action, reducer) : reducer, options, init),
        _useReducer2 = _slicedToArray(_useReducer, 2),
        state = _useReducer2[0],
        _dispatch = _useReducer2[1];

  const dispatch = dispatcher ? action => dispatcher(action, dispatchMiddleware(_dispatch), options) : dispatchMiddleware(_dispatch);

  const getMeta = meta => _objectSpread$2({
    counter: counter.current
  }, meta);

  const setData = function setData(data) {
    let callback = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : noop;

    if (isMounted.current) {
      dispatch({
        type: actionTypes.fulfill,
        payload: data,
        meta: getMeta()
      });
      callback();
    }

    return data;
  };

  const setError = function setError(error) {
    let callback = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : noop;

    if (isMounted.current) {
      dispatch({
        type: actionTypes.reject,
        payload: error,
        error: true,
        meta: getMeta()
      });
      callback();
    }

    return error;
  };

  const onResolve = options.onResolve,
        onReject = options.onReject;

  const handleResolve = count => data => count === counter.current && setData(data, () => onResolve && onResolve(data));

  const handleReject = count => error => count === counter.current && setError(error, () => onReject && onReject(error));

  const start = promiseFn => {
    if ("AbortController" in window) {
      abortController.current.abort();
      abortController.current = new window.AbortController();
    }

    counter.current++;
    return new Promise((resolve, reject) => {
      if (!isMounted.current) return;

      const executor = () => promiseFn().then(resolve, reject);

      dispatch({
        type: actionTypes.start,
        payload: executor,
        meta: getMeta()
      });
    });
  };

  const promise = options.promise,
        promiseFn = options.promiseFn,
        initialValue = options.initialValue;

  const load = () => {
    if (promise) {
      return start(() => promise).then(handleResolve(counter.current), handleReject(counter.current));
    }

    const isPreInitialized = initialValue && counter.current === 0;

    if (promiseFn && !isPreInitialized) {
      return start(() => promiseFn(options, abortController.current)).then(handleResolve(counter.current), handleReject(counter.current));
    }
  };

  const deferFn = options.deferFn;

  const run = function run() {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    if (deferFn) {
      lastArgs.current = args;
      return start(() => deferFn(args, options, abortController.current)).then(handleResolve(counter.current), handleReject(counter.current));
    }
  };

  const cancel = () => {
    counter.current++;
    abortController.current.abort();
    isMounted.current && dispatch({
      type: actionTypes.cancel,
      meta: getMeta()
    });
  };

  const watch = options.watch,
        watchFn = options.watchFn;
  useEffect(() => {
    if (watchFn && prevOptions.current && watchFn(options, prevOptions.current)) load();
  });
  useEffect(() => {
    promise || promiseFn ? load() : cancel();
  }, [promise, promiseFn, watch]);
  useEffect(() => () => isMounted.current = false, []);
  useEffect(() => () => abortController.current.abort(), []);
  useEffect(() => (prevOptions.current = options) && undefined);
  useDebugValue(state, _ref2 => {
    let status = _ref2.status;
    return "[".concat(counter.current, "] ").concat(status);
  });
  return useMemo(() => _objectSpread$2({}, state, {
    cancel,
    run,
    reload: () => lastArgs.current ? run(...lastArgs.current) : load(),
    setData,
    setError
  }), [state, deferFn, onResolve, onReject, dispatcher, reducer$1]);
};

const parseResponse = (accept, json) => res => {
  if (!res.ok) return Promise.reject(res);
  if (json === false) return res;
  if (json === true || accept === "application/json") return res.json();
  return res;
};

const useAsyncFetch = function useAsyncFetch(input, init) {
  let _ref = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

  let defer = _ref.defer,
      json = _ref.json,
      options = _objectWithoutProperties(_ref, ["defer", "json"]);

  const method = input.method || init && init.method;
  const headers = input.headers || init && init.headers || {};
  const accept = headers["Accept"] || headers["accept"] || headers.get && headers.get("accept");

  const doFetch = (input, init) => window.fetch(input, init).then(parseResponse(accept, json));

  const isDefer = defer === true || ~["POST", "PUT", "PATCH", "DELETE"].indexOf(method);
  const fn = defer === false || !isDefer ? "promiseFn" : "deferFn";
  const state = useAsync(_objectSpread$2({}, options, {
    [fn]: useCallback((_, props, ctrl) => doFetch(input, _objectSpread$2({
      signal: ctrl ? ctrl.signal : props.signal
    }, init)), [JSON.stringify(input), JSON.stringify(init)])
  }));
  useDebugValue(state, _ref3 => {
    let counter = _ref3.counter,
        status = _ref3.status;
    return "[".concat(counter, "] ").concat(status);
  });
  return state;
};

const unsupported = () => {
  throw new Error("useAsync requires React v16.8 or up. Upgrade your React version or use the <Async> component instead.");
};

var useAsync$1 = useEffect ? useAsync : unsupported;
const useFetch = useEffect ? useAsyncFetch : unsupported;
export default Async;
export { Fulfilled, Initial, Pending, Rejected, Settled, createInstance, statusTypes, useAsync$1 as useAsync, useFetch };